/* global define */
/* eslint-disable no-var, object-shorthand */
/**
 * Shared background resolution for the conversational theme.
 *
 * Consumed by the builder's live preview and by the front-end theme model, so
 * the rule deciding what each background mode paints — and the CSS composed
 * from it — lives in exactly one place. The preview exists to show the owner
 * what the front end will paint, so any drift between the two would make it
 * lie about the form it is previewing.
 *
 * Every value composed here is one the add-on could itself have produced: the
 * mode and gradient type are whitelisted, the angle is clamped, and a media
 * reference reaches url() only when its scheme is one a stylesheet may fetch.
 * The builder holds these settings to that grammar, but the builder is not the
 * only way one reaches storage — an import, a direct form-meta write or a
 * third-party filter all bypass it — so composition is the last boundary that
 * can hold a stored value to what the add-on means by it.
 */
define( 'shared/background',[], function () {
	/**
	 * The background modes the add-on knows how to paint.
	 *
	 * @type {Array}
	 */
	var MODES = [ 'color', 'gradient', 'image' ];

	/**
	 * The gradient types the add-on knows how to compose.
	 *
	 * @type {Array}
	 */
	var TYPES = [ 'linear', 'radial' ];

	/**
	 * The background settings holding a color rather than a mode or a number.
	 *
	 * Published so each consumer's setting reader knows which names to answer
	 * with an already-normalized color, without naming them a second time.
	 *
	 * @type {Array}
	 */
	var COLOR_SETTINGS = [
		'conversational_background_color',
		'conversational_gradient_primary',
		'conversational_gradient_secondary',
	];

	/**
	 * The URL forms a stylesheet may be asked to fetch.
	 *
	 * The WordPress media library only ever produces http, https, or a
	 * protocol-relative URL; anything else in a stored setting came from
	 * somewhere the add-on does not control and has no business in a
	 * background layer.
	 *
	 * @type {RegExp}
	 */
	var SAFE_URL = /^(?:https?:\/\/|\/\/)\S+$/i;

	/**
	 * Constrain a value to a list, falling back when it is not on it.
	 *
	 * @param {*}     value    Stored value.
	 * @param {Array} list     The values this setting may hold.
	 * @param {*}     fallback Shipped default for the setting.
	 * @return {string} An allowed value.
	 */
	function allowed( value, list, fallback ) {
		if ( -1 !== list.indexOf( String( value ) ) ) {
			return String( value );
		}

		return -1 !== list.indexOf( String( fallback ) )
			? String( fallback )
			: list[ 0 ];
	}

	/**
	 * Resolve a stored background mode to one the theme can paint.
	 *
	 * @param {*} value    Stored background mode.
	 * @param {*} fallback Shipped default mode.
	 * @return {string} 'color', 'gradient', or 'image'.
	 */
	function mode( value, fallback ) {
		return allowed( value, MODES, fallback );
	}

	/**
	 * Resolve a stored gradient angle to a paintable number of degrees.
	 *
	 * A CSS angle outside 0-360 is legal but is not something the panel's
	 * slider can produce, and a non-numeric value would compose `NaNdeg` and
	 * take the whole declaration down with it.
	 *
	 * @param {*} value    Stored angle.
	 * @param {*} fallback Shipped default angle.
	 * @return {number} Degrees in the 0-360 range.
	 */
	function angle( value, fallback ) {
		var degrees = parseFloat( value );

		if ( isNaN( degrees ) ) {
			degrees = parseFloat( fallback );
		}

		if ( isNaN( degrees ) ) {
			degrees = 0;
		}

		return Math.min( 360, Math.max( 0, degrees ) );
	}

	/**
	 * Compose a CSS gradient value from resolved appearance settings.
	 *
	 * @param {Object} options           Gradient options.
	 * @param {string} options.type      'linear' or 'radial'.
	 * @param {number} options.angle     Linear gradient angle in degrees.
	 * @param {string} options.primary   Primary color stop.
	 * @param {string} options.secondary Secondary color stop.
	 * @return {string} A CSS gradient function value.
	 */
	function gradient( options ) {
		if ( 'radial' === allowed( options.type, TYPES, 'linear' ) ) {
			return (
				'radial-gradient(circle, ' +
				options.primary +
				', ' +
				options.secondary +
				')'
			);
		}

		return (
			'linear-gradient(' +
			angle( options.angle, 0 ) +
			'deg, ' +
			options.primary +
			', ' +
			options.secondary +
			')'
		);
	}

	/**
	 * Compose a CSS background layer from a resolved media URL.
	 *
	 * Backslashes are escaped as well as double quotes because the URL lands
	 * inside a quoted CSS value: a stray quote, or a trailing backslash
	 * escaping the closing one, would leave the string unterminated and CSSOM
	 * would drop the whole declaration.
	 *
	 * @param {string} url Resolved media URL.
	 * @return {string} A CSS background value, or '' when the URL is not one
	 *                  a stylesheet may fetch.
	 */
	function image( url ) {
		var value = String( url || '' ).trim();

		if ( ! SAFE_URL.test( value ) ) {
			return '';
		}

		return (
			'url("' +
			value.replace( /\\/g, '\\\\' ).replace( /"/g, '\\"' ) +
			'") center / cover no-repeat'
		);
	}

	/**
	 * Build the background value for whichever mode the form is using.
	 *
	 * @param {Function} setting      Reads a form setting with a fallback.
	 * @param {Object}   defaults     The shipped defaults.
	 * @param {Function} resolveMedia Resolves a media setting to a URL.
	 * @return {string} A CSS background value.
	 */
	function resolve( setting, defaults, resolveMedia ) {
		var current = mode(
			setting(
				'conversational_background_mode',
				defaults.conversational_background_mode
			),
			defaults.conversational_background_mode
		);
		var color;
		var layer;

		if ( 'gradient' === current ) {
			return gradient( {
				type: setting(
					'conversational_gradient_type',
					defaults.conversational_gradient_type
				),
				angle: setting(
					'conversational_gradient_angle',
					defaults.conversational_gradient_angle
				),
				primary: setting(
					'conversational_gradient_primary',
					defaults.conversational_gradient_primary
				),
				secondary: setting(
					'conversational_gradient_secondary',
					defaults.conversational_gradient_secondary
				),
			} );
		}

		color = setting(
			'conversational_background_color',
			defaults.conversational_background_color
		);

		if ( 'image' === current ) {
			// A media reference the theme cannot paint — unset, or carrying a
			// scheme image() rejects — leaves the flat color showing rather
			// than a transparent surface with text over the page behind it.
			layer = image(
				resolveMedia( setting( 'conversational_background_image', '' ) )
			);

			return layer || color;
		}

		return color;
	}

	return {
		colorSettings: COLOR_SETTINGS,
		mode: mode,
		angle: angle,
		resolve: resolve,
	};
} );

